import React from 'react';
import { take, put, fork, cancel } from 'redux-saga/effects';
import { FormattedMessage } from 'react-intl';

import ConnectionManager from 'ConnectionManager';
import * as currentUserActions from 'data/CurrentUser/actions';

import * as actions from './actions';
import * as constants from './constants';
import messages from './messages';

import authorPluginGet from './commands/authorPluginGet';
import authorPluginVersionDelete from './commands/authorPluginVersionDelete';
import authorPluginVersionUnpublish from './commands/authorPluginVersionUnpublish';
import iamSubscriptionUserList from './commands/iamSubscriptionUserList';
import iamSubscriptionTeamList from './commands/iamSubscriptionTeamList';
import authorPluginList from './commands/authorPluginList';
import workspacePluginList from './commands/workspacePluginList';
import workspacePluginVersionList from './commands/workspacePluginVersionList';
import workspacePluginDetails from './commands/workspacePluginDetails';
import workspacePluginUpdate from './commands/workspacePluginUpdate';
import workspacePluginCreate from './commands/workspacePluginCreate';
import workspacePluginCollaboratorSummary from './commands/workspacePluginCollaboratorSummary';
import workspacePluginPermissionGrant from './commands/workspacePluginPermissionGrant';
import workspacePluginPermissionRevoke from './commands/workspacePluginPermissionRevoke';
import workspaceCohortList from './commands/workspaceCohortList';
import workspaceCohort from './commands/workspaceCohort';
import workspaceCohortCreate from './commands/workspaceCohortCreate';
import workspaceCohortChange from './commands/workspaceCohortChange';
import workspaceCohortCollaboratorSummary from './commands/workspaceCohortCollaboratorSummary';
import workspaceCohortEnrollments from './commands/workspaceCohortEnrollments';
import workspaceCohortPermissionGrant from './commands/workspaceCohortPermissionGrant';
import workspaceCohortPermissionRevoke from './commands/workspaceCohortPermissionRevoke';
import workspaceAccountCreate from './commands/workspaceAccountCreate';
import workspaceAccountsList from './commands/workspaceAccountsList';
import iamAccessRequestList from './commands/iamAccessRequestList';
import iamTeamCreate from './commands/iamTeamCreate';
import iamTeamList from './commands/iamTeamList';
import iamTeamAccountSummary from './commands/iamTeamAccountSummary';
import iamTeamPermissionGrant from './commands/iamTeamPermissionGrant';
import iamTeamPermissionRevoke from './commands/iamTeamPermissionRevoke';
import workspaceCohortArchive from './commands/workspaceCohortArchive';
import workspaceWorkspacesList from './commands/workspaceWorkspacesList';
import workspaceCohortAccountEnroll from './commands/workspaceCohortAccountEnroll';
import workspaceCreate from './commands/workspaceCreate';
import workspacePermissionGrant from './commands/workspacePermissionGrant';
import workspacePermissionRevoke from './commands/workspacePermissionRevoke';
import workspaceCollaboratorSummary from './commands/workspaceCollaboratorSummary';
import workspaceDeploymentsList from './commands/workspaceDeploymentsList';
import workspaceActivityPublish from './commands/workspaceActivityPublish';
import projectActivityPublish from './commands/projectActivityPublish';
import workspaceCoursewareMetaInfoGet from './commands/workspaceCoursewareMetaInfoGet';
import workspaceCoursewareMetaInfoSet from './commands/workspaceCoursewareMetaInfoSet';
import workspaceProjectList from './commands/workspaceProjectList';
import workspaceProjectCreate from './commands/workspaceProjectCreate';
import workspacePublicationsList from './commands/workspacePublicationsList';
import manualGradeConfiguration from './ManualGrading/saga';
import evaluable from './Evaluable/saga';

import workspacePluginDelete from './commands/workspacePluginDelete';
import workspaceProjectIngestList from './commands/workspaceProjectIngestList';
import workspaceProjectIngestGet from './commands/workspaceProjectIngestGet';
import { setConnection } from './rtmConnectionRef';

export default function* handleConnection() {
  while (true) { // eslint-disable-line no-constant-condition
    const { remember, connection } = yield take(constants.CONNECT);
    setConnection(connection);
    try {
      /* *********** Command Listeners *********** */
      const manualGradeConfigurationTask = yield fork(manualGradeConfiguration, connection);
      const evaluableTask = yield fork(evaluable, connection);
      const authorPluginGetTask = yield fork(authorPluginGet, connection);
      const iamSubscriptionUserListTask = yield fork(iamSubscriptionUserList, connection);
      const authorPluginListTask = yield fork(authorPluginList, connection);
      const workspacePluginListTask = yield fork(workspacePluginList, connection);
      const workspacePluginVersionListTask = yield fork(workspacePluginVersionList, connection);
      const workspacePluginDetailsTask = yield fork(workspacePluginDetails, connection);
      const workspacePluginUpdateTask = yield fork(workspacePluginUpdate, connection);
      const workspacePluginCreateTask = yield fork(workspacePluginCreate, connection);
      const workspacePluginCollaboratorSummaryTask = yield fork(workspacePluginCollaboratorSummary, connection);
      const workspacePluginPermissionGrantTask = yield fork(workspacePluginPermissionGrant, connection);
      const workspacePluginPermissionRevokeTask = yield fork(workspacePluginPermissionRevoke, connection);
      const workspaceCohortListTask = yield fork(workspaceCohortList, connection);
      const workspaceCohortTask = yield fork(workspaceCohort, connection);
      const workspaceCohortCreateTask = yield fork(workspaceCohortCreate, connection);
      const workspaceCohortCollaboratorSummaryTask = yield fork(workspaceCohortCollaboratorSummary, connection);
      const workspaceCohortEnrollmentsTask = yield fork(workspaceCohortEnrollments, connection);
      const workspaceCohortPermissionGrantTask = yield fork(workspaceCohortPermissionGrant, connection);
      const workspaceCohortPermissionRevokeTask = yield fork(workspaceCohortPermissionRevoke, connection);
      const workspaceAccountCreateTask = yield fork(workspaceAccountCreate, connection);
      const workspaceProjectListTask = yield fork(workspaceProjectList, connection);
      const workspaceProjectCreateTask = yield fork(workspaceProjectCreate, connection);
      const workspacePublicationsListTask = yield fork(workspacePublicationsList, connection);
      const iamTeamCreateTask = yield fork(iamTeamCreate, connection);
      const iamTeamListTask = yield fork(iamTeamList, connection);
      const workspaceCohortArchiveTask = yield fork(workspaceCohortArchive, connection);
      const workspaceAccountsListTask = yield fork(workspaceAccountsList, connection);
      const iamAccessRequestListTask = yield fork(iamAccessRequestList, connection);
      const workspaceWorkspacesListTask = yield fork(workspaceWorkspacesList, connection);
      const workspacePermissionGrantTask = yield fork(workspacePermissionGrant, connection);
      const workspacePermissionRevokeTask = yield fork(workspacePermissionRevoke, connection);
      const workspaceCollaboratorSummaryTask = yield fork(workspaceCollaboratorSummary, connection);
      const iamSubscriptionTeamListTask = yield fork(iamSubscriptionTeamList, connection);
      const iamTeamAccountSummaryTask = yield fork(iamTeamAccountSummary, connection);
      const iamTeamPermissionGrantTask = yield fork(iamTeamPermissionGrant, connection);
      const iamTeamPermissionRevokeTask = yield fork(iamTeamPermissionRevoke, connection);
      const workspaceCreateTask = yield fork(workspaceCreate, connection);
      const workspaceCohortChangeTask = yield fork(workspaceCohortChange, connection);
      const workspaceCohortAccountEnrollTask = yield fork(workspaceCohortAccountEnroll, connection);
      const workspaceDeploymentsListTask = yield fork(workspaceDeploymentsList, connection);
      const workspaceActivityPublishTask = yield fork(workspaceActivityPublish, connection);
      const projectActivityPublishTask = yield fork(projectActivityPublish, connection);
      const workspaceCoursewareMetaInfoGetTask = yield fork(workspaceCoursewareMetaInfoGet, connection);
      const workspaceCoursewareMetaInfoSetTask = yield fork(workspaceCoursewareMetaInfoSet, connection);
      const workspacePluginDeleteTask = yield fork(workspacePluginDelete, connection);
      const authorPluginVersionDeleteTask = yield fork(authorPluginVersionDelete, connection);
      const authorPluginVersionUnpublishTask = yield fork(authorPluginVersionUnpublish, connection);
      const workspaceProjectIngestListTask = yield fork(workspaceProjectIngestList, connection);
      const workspaceProjectIngestGetTask = yield fork(workspaceProjectIngestGet, connection);
      /* *********** Command Listeners *********** */

      const {
        bearerToken,
        expiry,
      } = connection.getBearerInfo();
      const connectionManager = new ConnectionManager();
      connectionManager.connection = connection;
      // Expiry is RFC-1123, which parse can easily parse.
      // https://dev-developers.smartsparrow.com/messages/authenticate-ok/
      const expiryAsDate = expiry && new Date(Date.parse(expiry));

      yield put(actions.serverConnectSuccess(bearerToken));
      yield put(currentUserActions.setCurrentUser(bearerToken, remember, expiryAsDate));
      yield put(actions.commandWorkspaceWorkspacesList());
      try {
        const responseMsg = yield connection.fetch(constants.RTM_ME_GET);
        yield put(currentUserActions.setCurrentMe(responseMsg.response && responseMsg.response.account));
      } catch (error) {
        yield put(currentUserActions.unsetCurrentMe());
      }

      // Listen for disconnect
      // TODO disconnect to be handled via new ConnectionManager
      yield take(constants.DISCONNECT);

      setConnection(null);
      yield cancel(manualGradeConfigurationTask);
      yield cancel(evaluableTask);
      yield cancel(authorPluginGetTask);
      yield cancel(iamSubscriptionUserListTask);
      yield cancel(authorPluginListTask);
      yield cancel(workspacePluginListTask);
      yield cancel(workspacePluginVersionListTask);
      yield cancel(workspacePluginDetailsTask);
      yield cancel(workspacePluginUpdateTask);
      yield cancel(workspacePluginCreateTask);
      yield cancel(workspacePluginCollaboratorSummaryTask);
      yield cancel(workspacePluginPermissionGrantTask);
      yield cancel(workspacePluginPermissionRevokeTask);
      yield cancel(workspaceCohortListTask);
      yield cancel(workspaceCohortTask);
      yield cancel(workspaceCohortCreateTask);
      yield cancel(workspaceCohortCollaboratorSummaryTask);
      yield cancel(workspaceCohortEnrollmentsTask);
      yield cancel(workspaceCohortPermissionGrantTask);
      yield cancel(workspaceCohortPermissionRevokeTask);
      yield cancel(workspaceAccountCreateTask);
      yield cancel(workspaceProjectListTask);
      yield cancel(workspaceProjectCreateTask);
      yield cancel(workspacePublicationsListTask);
      yield cancel(iamTeamCreateTask);
      yield cancel(iamTeamListTask);
      yield cancel(workspaceCohortArchiveTask);
      yield cancel(workspaceAccountsListTask);
      yield cancel(iamAccessRequestListTask);
      yield cancel(workspaceWorkspacesListTask);
      yield cancel(workspacePermissionGrantTask);
      yield cancel(workspacePermissionRevokeTask);
      yield cancel(workspaceCollaboratorSummaryTask);
      yield cancel(iamSubscriptionTeamListTask);
      yield cancel(iamTeamAccountSummaryTask);
      yield cancel(iamTeamPermissionGrantTask);
      yield cancel(iamTeamPermissionRevokeTask);
      yield cancel(workspaceCreateTask);
      yield cancel(workspaceCohortChangeTask);
      yield cancel(workspaceCohortAccountEnrollTask);
      yield cancel(workspaceDeploymentsListTask);
      yield cancel(workspaceActivityPublishTask);
      yield cancel(projectActivityPublishTask);
      yield cancel(workspaceCoursewareMetaInfoGetTask);
      yield cancel(workspaceCoursewareMetaInfoSetTask);
      yield cancel(workspacePluginDeleteTask);
      yield cancel(authorPluginVersionDeleteTask);
      yield cancel(authorPluginVersionUnpublishTask);
      yield cancel(workspaceProjectIngestListTask);
      yield cancel(workspaceProjectIngestGetTask);
      yield put(currentUserActions.unsetCurrentUser());
      connectionManager.closeConnection();
    } catch (error) {
      yield put(actions.serverConnectError(<FormattedMessage {...messages.defaultError} />));
    }
  }
}
